import { useEffect, useState } from 'react';
import { Button, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, EmptyState } from '../../../shared/components';
import { EditIcon, TrashIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { CalendarStagingRow } from '../../../shared/services/humanResources/types';

const repo = getHumanResourcesRepository();
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * Pestaña "Pendiente de revisión" - tabla TEMPORAL (staging) donde cae la
 * lectura automática de una imagen ANTES de tocar el calendario final.
 * Filtros por año/mes/estado, CRUD por fila, y el botón "Confirmar bien
 * procesado" que mueve un mes completo a la tabla final (ver
 * confirmMonth en el backend - hace upsert, así que también sirve para
 * RECTIFICAR un mes ya cargado sin duplicar).
 */
export function StagingReviewTab({ year }: { year: number }) {
  const toast = useToast();
  const [month, setMonth] = useState<number | ''>('');
  const [status, setStatus] = useState<'' | 'No revisado' | 'Revisado' | 'Procesado'>('');
  const [rows, setRows] = useState<CalendarStagingRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleting, setDeleting] = useState<CalendarStagingRow | null>(null);
  const [confirmingMonth, setConfirmingMonth] = useState<number | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listStaging({ sourceYear: year, sourceMonth: month || undefined, status: status || undefined }).then((r) => { setRows(r); setIsLoading(false); });
  };
  useEffect(() => { load(); }, [year, month, status]);

  const cycleDayType = async (row: CalendarStagingRow) => {
    await repo.updateStagingRow(row.id, { dayType: row.dayType === 'Festivo' ? 'Ordinario' : 'Festivo' });
    load();
  };
  const markReviewed = async (row: CalendarStagingRow) => {
    await repo.updateStagingRow(row.id, { status: row.status === 'Revisado' ? 'No revisado' : 'Revisado' });
    load();
  };

  const monthsPending = Array.from(new Set(rows.filter((r) => r.status !== 'Procesado').map((r) => r.sourceMonth))).sort((a, b) => a - b);

  const confirmMonth = async (m: number) => {
    const count = await repo.confirmMonth(year, m);
    toast.show(`${count} días de ${MONTHS[m - 1]} confirmados y cargados al calendario final.`);
    setConfirmingMonth(null);
    load();
  };

  const monthOptions = [{ value: '', label: 'Todos los meses' }, ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))];
  const statusOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'No revisado', label: 'No revisado' },
    { value: 'Revisado', label: 'Revisado' },
    { value: 'Procesado', label: 'Procesado' },
  ];

  const columns: Column<CalendarStagingRow>[] = [
    { key: 'date', header: 'Día', render: (r) => r.calendarDate, sortable: true, sortValue: (r) => r.calendarDate },
    { key: 'weekday', header: 'Día Sem', render: (r) => r.dayOfWeek },
    {
      key: 'type',
      header: 'Tipo Día',
      render: (r) =>
        r.status === 'Procesado' ? (
          <Badge tone={r.dayType === 'Festivo' ? 'warning' : 'neutral'}>{r.dayType}</Badge>
        ) : (
          <button onClick={() => cycleDayType(r)}>
            <Badge tone={r.dayType === 'Festivo' ? 'warning' : 'neutral'}>{r.dayType}</Badge>
          </button>
        ),
    },
    { key: 'quincena', header: 'Quincena', render: (r) => r.quincena },
    { key: 'confidence', header: 'Confianza lectura', render: (r) => (r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—') },
    {
      key: 'status',
      header: 'Estado',
      render: (r) =>
        r.status === 'Procesado' ? (
          <Badge tone="success">Procesado</Badge>
        ) : (
          <button onClick={() => markReviewed(r)}>
            <Badge tone={r.status === 'Revisado' ? 'success' : 'neutral'}>{r.status}</Badge>
          </button>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (r.status === 'Procesado' ? null : <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Select label="Mes" options={monthOptions} value={String(month)} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')} />
        <Select label="Estado" options={statusOptions} value={status} onChange={(e) => setStatus(e.target.value as typeof status)} />
      </div>

      {monthsPending.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {monthsPending.map((m) => (
            <Button key={m} size="md" variant="outline" onClick={() => setConfirmingMonth(m)}>
              Confirmar {MONTHS[m - 1]} bien procesado
            </Button>
          ))}
        </div>
      )}

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🕐" title="Sin días pendientes de revisión" description="Carga un calendario por imagen desde 'Calendario' para empezar." />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin filas" />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar fila de revisión"
        description={`¿Eliminar el ${deleting?.calendarDate} de la revisión? No se verá afectado el calendario final si este día ya fue confirmado antes.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteStagingRow(deleting.id);
          setDeleting(null);
          load();
        }}
      />

      <ConfirmDialog
        open={confirmingMonth !== null}
        title={`Confirmar ${confirmingMonth ? MONTHS[confirmingMonth - 1] : ''} bien procesado`}
        description="Esto copia todos los días de este mes a la tabla final de calendario (la que usa el motor de cálculo de Horas Extra). Si el mes ya estaba cargado antes, se actualiza sin duplicar."
        confirmLabel="Confirmar"
        onCancel={() => setConfirmingMonth(null)}
        onConfirm={() => confirmingMonth && confirmMonth(confirmingMonth)}
      />
    </div>
  );
}
